<template>
	<div class="bg-white w-full">

    <div class="group">
      <div class="caption">
        <h5 class="text-base font-medium text-green-dark">Sanering</h5>
      </div>
      <div class="fields flex flex-col w-full px-6 pt-6">
        <div class="flex flex-wrap w-full mx-0 sm:-mx-2">
          <field-group :isAllowed="hasPermission" :value="order.sanmer && order.sanmer.name" label="Saneerder" class="w-full sm:w-1/3 px-0 sm:px-2 mb-4 md:mb-0">
            <div class="relative">
              <select id="sanmer" v-model="sanmerId" class="bg-white cursor-pointer select pr-6">
                <option value="">Selecteer een saneerder</option>
                <option v-for="sanmer in sanmers" :key="sanmer.id" :value="sanmer.id">
                  {{ sanmer.name }}
                </option>
              </select>
              <div class="pointer-events-none absolute pin-y pin-r flex items-center px-2 text-grey-darker">
                <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </field-group>

          <field-group :isAllowed="hasPermission" :value="order.sanitation_date && $moment(order.sanitation_date).format('DD-MM-YYYY')" label="Datum sanering" class="w-full sm:w-1/3 px-0 sm:px-2 mb-4 md:mb-0">
            <datepicker id="datum-sanering" placeholder="dd-mm-yyyy" calendar-class="datepicker"
              :value="sanitiationDate" @input="dateSelected($event, 'sanitation_date')" :format="formatter">
            </datepicker>
          </field-group>

          <field-group :isAllowed="hasPermission" :value="order.sanitation_unsubscribe_date && $moment(order.sanitation_unsubscribe_date).format('DD-MM-YYYY')" label="Datum afmelding sanering" class="w-full sm:w-1/3 px-0 sm:px-2">
            <datepicker id="datum-afmelding-sanering" placeholder="dd-mm-yyyy" calendar-class="datepicker"
              :value="sanitationUnsubscribeDate" @input="dateSelected($event, 'sanitation_unsubscribe_date')" :format="formatter" :language="dutch"></datepicker>
          </field-group>

        </div>
      </div>
    </div>

    <div class="group">
      <div class="caption flex justify-between items-center py-4">
        <h5 class="text-base font-medium text-green-dark">Vrijgaverapporten</h5>
        <button type="button" v-if="hasPermission" class="btn btn-primary btn-sm" @click="toggleDocumentModal('release-report')">+ vrijgaverapport</button>
      </div>
      <div class="fields flex flex-col w-full p-6">
        <div class="flex flex-wrap -mx-2 -mb-4" v-if="releaseReports.length">
          <div class="w-full sm:w-1/2 px-2 mb-4" v-for="document in releaseReports" :key="document.id">
            <file-viewer :file="document" @remove="deleteFile" type="release-report" class="border"></file-viewer>
          </div>
        </div>
        <div v-else class="alert alert-info" role="alert">
          <span class="title">Geen vrijgaverapporten aanwezig</span>
        </div>
      </div>
    </div>

    <div class="group">
      <div class="caption flex justify-between items-center py-4">
        <h5 class="text-base font-medium text-green-dark">Stortbonnen</h5>
        <button type="button" v-if="hasPermission" class="btn btn-primary btn-sm" @click="toggleDocumentModal('deposit-slip')">+ stortbon</button>
      </div>
      <div class="fields flex flex-col w-full p-6">
        <div class="flex flex-wrap -mx-2 -mb-4" v-if="depositSlips.length">
          <div class="w-full sm:w-1/2 px-2 mb-4" v-for="document in depositSlips" :key="document.id">
            <file-viewer :file="document" @remove="deleteFile" type="deposit-slip" class="border"></file-viewer>
          </div>
        </div>
        <div v-else class="alert alert-info" role="alert">
          <span class="title">Geen stortbonnen aanwezig</span>
        </div>
      </div>
    </div>
	</div>
</template>

<script>
import ApiService from '@/common/api.service';
import SanmerService from '@/services/sanmer';
import FieldGroup from '@/components/globals/FieldGroup';
import FileViewer from '@/components/globals/FileViewer';
import Datepicker from 'vuejs-datepicker/dist/vuejs-datepicker.esm.js';

import { API_URL } from '@/common/config';
import { mapState } from 'vuex';
import { nl } from 'vuejs-datepicker/dist/locale';

const RELEASE_REPORTS = 'release-report';
const DEPOSIT_SLIPS = 'deposit-slip';

export default {
  name: 'TabDocuments',
  components: {
    FieldGroup,
    FileViewer,
    Datepicker
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    hasPermission: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      sanmers: [],
      showDocumentModal: false,
      dutch: nl
    };
  },
  computed: {
    ...mapState('modal', ['items']),
    sanmerId: {
      get() {
        return this.order.sanmer != null ? this.order.sanmer.id : '';
      },
      set(newValue) {
        if (this.order.sanmer == null) {
          this.order.sanmer = {};
        }
        this.$set(this.order.sanmer, 'id', newValue);
      }
    },
    depositSlips() {
      const type = 'deposit_slips';

      if (this.items && this.items.files && this.items.type == DEPOSIT_SLIPS) {
        this.set(type);
      }
      return this.order[type];
    },
    releaseReports() {
      const type = 'release_reports';

      if (
        this.items &&
        this.items.files &&
        this.items.type == RELEASE_REPORTS
      ) {
        this.set(type);
      }
      return this.order[type];
    },
    sanitiationDate() {
      if (this.order && this.order.sanitation_date) {
        return this.$moment(this.order.sanitation_date).toDate();
      }
    },
    sanitationUnsubscribeDate() {
      if (this.order && this.order.sanitation_unsubscribe_date) {
        return this.$moment(this.order.sanitation_unsubscribe_date).toDate();
      }
    }
  },
  async created() {
    await this.fetchSanmers();
  },
  methods: {
    set(type) {
      this.items.files.forEach(file => {
        const index = this.order[type].findIndex(item => item.id === file.id);
        if (index === -1) {
          this.order[type].push(file);
        }
      });
      this.$store.commit('modal/setItems', null);
    },
    dateSelected(date, property) {
      this.$set(
        this.order,
        property,
        this.$moment(date).format('Y-MM-DD HH:mm:ss')
      );
    },
    formatter(date) {
      return this.$moment(date).format('DD-MM-YYYY');
    },
    toggleDocumentModal(type) {
      this.$store.commit('modal/setItems', { id: this.order.id, type: type });
      this.$store.commit('modal/show', 'DocumentModal');
    },
    async fetchSanmers() {
      this.sanmers = await SanmerService.query({ limit: 100 })
        .then(response => response.data.data)
        .catch(() => []);
    },
    async deleteFile(response) {
      const type =
        response.type == RELEASE_REPORTS ? 'release_reports' : 'deposit_slips';
      const fileId = response.file.id;

      try {
        await ApiService.delete(`/v1/files/${fileId}`);
        const index = this.order[type].findIndex(item => item.id == fileId);
        this.order[type].splice(index, 1);
      } catch (error) {
        console.log(error);
      }
    },
    async requestFileUrl(type, fileId) {
      const { data } = await ApiService.get(`/v1/files/${fileId}/hash`);
      window.open(`${API_URL}/files/${type}/${data}`);
    }
  }
};
</script>

<style scoped>
#sanmer {
  padding-right: 1.75rem;
}
</style>
